import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./css/playerDataForm.css";
import useTranslation from "../hooks/useTranslation";
import { db } from "../firebase";
import { ref, push, set } from "firebase/database";

function PlayerDataForm({ selectedCharacters, updateProgress }) {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  
  // Función helper para obtener el nombre traducido del personaje
  const getCharacterName = (characterId) => {
    return t(`characterSelection.characters.${characterId}.name`);
  };
  
  // Cargar datos guardados de localStorage si existen
  const [playersData, setPlayersData] = useState(() => {
    const saved = localStorage.getItem('greenscape_players_data');
    return saved ? JSON.parse(saved) : {};
  });
  
  // Obtener array de jugadores ordenado
  const playersArray = selectedCharacters?.playersArray || [];
  const currentPlayer = playersArray[currentPlayerIndex];
  const totalPlayers = playersArray.length;
  
  // Estado del formulario actual
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    town: '',
    city: '',
    country: '',
    deviceType: '',
    startDate: ''
  });

  // Detectar tipo de dispositivo automáticamente
  useEffect(() => {
    const detectDevice = () => {
      const ua = navigator.userAgent;
      if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return "Tablet";
      }
      if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
        return "Smartphone";
      }
      return "Desktop/Laptop";
    };

    const getCurrentDate = () => {
      const now = new Date();
      return now.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    setFormData(prev => ({
      ...prev,
      deviceType: detectDevice(),
      startDate: getCurrentDate()
    }));
  }, []);

  // Cargar datos del jugador si ya fueron ingresados previamente
  useEffect(() => {
    if (currentPlayer && playersData[currentPlayer.characterId]) {
      setFormData(playersData[currentPlayer.characterId]);
    } else {
      // Resetear formulario pero mantener device y date
      setFormData(prev => ({
        age: '',
        gender: '',
        town: '',
        city: '',
        country: '',
        deviceType: prev.deviceType,
        startDate: prev.startDate
      }));
    }
  }, [currentPlayerIndex, currentPlayer, playersData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isFormValid = () => {
    const baseValid =
      formData.age.trim() !== '' &&
      formData.gender.trim() !== '' &&
      formData.town.trim() !== '' &&
      formData.country.trim() !== '';

    // En búlgaro no se pide ciudad (град se usa solo una vez)
    if (language === "bg") {
      return baseValid;
    }

    return baseValid && formData.city.trim() !== '';
  };

  const handleNext = async () => {
    if (!isFormValid()) {
      alert(t('playerDataForm.validationAlert'));
      return;
    }

    // Guardar datos del jugador actual en estado/localStorage
    const updatedPlayersData = {
      ...playersData,
      [currentPlayer.characterId]: {
        ...formData,
        characterId: currentPlayer.characterId,
        characterName: getCharacterName(currentPlayer.characterId),
        playerNumber: currentPlayer.playerNumber
      }
    };

    setPlayersData(updatedPlayersData);
    
    // Guardar en localStorage siempre que se completa un jugador
    localStorage.setItem('greenscape_players_data', JSON.stringify(updatedPlayersData));

    // Enviar datos del jugador actual a Firebase Realtime Database (playerForms)
    try {
      const playerFormsRef = ref(db, "playerForms");
      // Generar referencia con clave automática para poder usarla como id
      const newPlayerRef = push(playerFormsRef);
      const id = newPlayerRef.key;

      const payload = {
        id,
        // Campos solicitados en español
        edad: formData.age,
        genero: formData.gender,
        localidad: formData.town,
        ciudad: language === "bg" ? "" : formData.city,
        pais: formData.country,
        tipo_device: formData.deviceType,
        created_at: new Date().toISOString(),
        // Información adicional útil para análisis
        characterId: currentPlayer.characterId,
        characterName: getCharacterName(currentPlayer.characterId),
        playerNumber: currentPlayer.playerNumber,
        language,
        start_date: formData.startDate
      };

      // Guardar el payload en la referencia recién creada
      await set(newPlayerRef, payload);
    } catch (error) {
      // No interrumpimos el flujo del juego si falla la escritura
      // para no romper la experiencia del usuario
      // eslint-disable-next-line no-console
      console.error("Error al guardar datos de jugador en Firebase:", error);
    }

    // Si es el último jugador, continuar al juego
    if (currentPlayerIndex === totalPlayers - 1) {
      updateProgress('player-data-form');
      navigate('/awareness');
    } else {
      // Pasar al siguiente jugador
      setCurrentPlayerIndex(currentPlayerIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPlayerIndex > 0) {
      setCurrentPlayerIndex(currentPlayerIndex - 1);
    }
  };

  if (!currentPlayer) {
    return (
      <div className="player-data-container">
        <div className="error-message">
          <h2>{t('playerDataForm.errorNoPlayers')}</h2>
          <button onClick={() => navigate('/character-selection')}>
            {t('playerDataForm.goBackButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="player-data-container">
      {/* Header con información del progreso y personaje */}
      <div className="progress-header">
        <div className="progress-info">
          <div 
            className="character-badge"
            style={{ backgroundColor: currentPlayer.color }}
          >
            <div className="character-avatar-small">
              <img 
                src={currentPlayer.image} 
                alt={getCharacterName(currentPlayer.characterId)}
              />
            </div>
            <span className="character-name-small">{getCharacterName(currentPlayer.characterId)}</span>
          </div>
          <span className="player-counter">
            {t('playerDataForm.playerProgress', { current: currentPlayerIndex + 1, total: totalPlayers })}
          </span>
        </div>
      </div>

      {/* Introducción al formulario */}
      <div className="form-intro">
        <h3>{t('playerDataForm.introTitle')}</h3>
        <p>
          {t('playerDataForm.introParagraph1')}
        </p>
        <p>
          {t('playerDataForm.introParagraph2')}
        </p>
        <p className="thank-you">{t('playerDataForm.introParagraph3')}</p>
      </div>

      {/* Formulario */}
      <form className="player-form" onSubmit={(e) => e.preventDefault()}>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="age">{t('playerDataForm.fields.age.label')}</label>
            <input
              type="number"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleInputChange}
              placeholder={t('playerDataForm.fields.age.placeholder')}
              min="1"
              max="120"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="gender">{t('playerDataForm.fields.gender.label')}</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              required
            >
              <option value="" disabled>{t('playerDataForm.fields.gender.placeholder')}</option>
              <option value="male">{t('playerDataForm.fields.gender.options.male')}</option>
              <option value="female">{t('playerDataForm.fields.gender.options.female')}</option>
              <option value="non-binary">{t('playerDataForm.fields.gender.options.nonBinary')}</option>
              <option value="prefer-not-to-say">{t('playerDataForm.fields.gender.options.preferNotToSay')}</option>
              <option value="other">{t('playerDataForm.fields.gender.options.other')}</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="town">{t('playerDataForm.fields.town.label')}</label>
            <input
              type="text"
              id="town"
              name="town"
              value={formData.town}
              onChange={handleInputChange}
              placeholder={t('playerDataForm.fields.town.placeholder')}
              required
            />
          </div>

          {language !== "bg" && (
            <div className="form-group">
              <label htmlFor="city">{t('playerDataForm.fields.city.label')}</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder={t('playerDataForm.fields.city.placeholder')}
                required
              />
            </div>
          )}

          <div className="form-group full-width">
            <label htmlFor="country">{t('playerDataForm.fields.country.label')}</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              placeholder={t('playerDataForm.fields.country.placeholder')}
              required
            />
          </div>

          {/* Campo de tipo de dispositivo seleccionable */}
          <div className="form-group">
            <label htmlFor="deviceType">{t('playerDataForm.fields.deviceType.label')}</label>
            <select
              id="deviceType"
              name="deviceType"
              value={formData.deviceType}
              onChange={handleInputChange}
              required
            >
              <option value="" disabled>
                {t('playerDataForm.fields.deviceType.placeholder')}
              </option>
              <option value="Desktop/Laptop">Desktop/Laptop</option>
              <option value="Smartphone">Smartphone</option>
              <option value="Tablet">Tablet</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="startDate">{t('playerDataForm.fields.startDate.label')}</label>
            <input
              type="text"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              readOnly
              className="readonly-field"
            />
          </div>
        </div>

        {/* Botones de navegación */}
        <div className="form-actions">
          {currentPlayerIndex > 0 && (
            <button 
              type="button"
              className="btn-previous"
              onClick={handlePrevious}
            >
              {t('playerDataForm.previousButton')}
            </button>
          )}
          
          <button 
            type="button"
            className={`btn-next ${isFormValid() ? 'active' : 'disabled'}`}
            onClick={handleNext}
            disabled={!isFormValid()}
            style={{ 
              backgroundColor: isFormValid() ? currentPlayer.color : '#9E9E9E' 
            }}
          >
            {currentPlayerIndex === totalPlayers - 1 ? t('playerDataForm.startGameButton') : t('playerDataForm.nextButton')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PlayerDataForm;

